import { shallowEqual } from 'react-redux';
import { useAppSelector } from 'state/store-hooks';
import {
  BalanceLimitState,
  CurrencyRates,
  resolveBalanceLimitState,
} from '@pano-project/utils/balanceLimit';

type AvailableCurrency = { code?: string; rate?: number | string | null };

const toRates = (currencies: AvailableCurrency[] | undefined): CurrencyRates =>
  Object.fromEntries(
    (currencies || []).filter((c) => c?.code).map((c) => [c.code, { rate: c.rate }]),
  );

// The minimum transaction limit, expressed in the base currency the same way
// the validation routines express it before comparing it with the balance:
// the selected gateway's limit for the payment currency, divided by its rate.
// No minimum applies when limits are overridden or for an authorization.
const toMinimumInBaseCurrency = (state: any): number | null => {
  if (state.virtualSettings?.override_limits || state.gatewayInfo?.is_auth) return null;

  const currency = state.payData?.currency;
  const currencyInfo = currency
    ? state.currentGateway?.payment_information?.currency_list?.[currency]
    : undefined;
  const min = Number(currencyInfo?.min_amount);
  const rate = Number(currencyInfo?.rate);
  if (!currencyInfo || !Number.isFinite(min) || !Number.isFinite(rate) || rate === 0) return null;

  return min / rate;
};

/**
 * Whether the balance limit of a withdrawal is broken, derived from the same
 * state the amount area uses: the session balance and its currency, the
 * transaction type, the entered amount and the available rates. It is the
 * screen-wide signal, so both the amount area and the header read it, in the
 * way the header already derives the active tab. A deposit, a session without
 * a balance and a blank amount report nothing.
 */
export const useBalanceLimit = (): BalanceLimitState => {
  const input = useAppSelector(
    (state: any) => ({
      transactionType: state.profileInfo?.transaction_type,
      balance: state.initTheme?.sessionInformation?.balance,
      balanceCurrency:
        state.initTheme?.sessionInformation?.currency || state.profileInfo?.base_currency,
      baseCurrency: state.profileInfo?.base_currency,
      amount: state.payData?.amount,
      minimumAmount: toMinimumInBaseCurrency(state),
      currencies: state.profileInfo?.available_currencies?.currencies,
    }),
    shallowEqual,
  );

  const { balanceErrorType, isBalanceLimitBroken } = resolveBalanceLimitState({
    transactionType: input.transactionType,
    balance: input.balance,
    balanceCurrency: input.balanceCurrency,
    baseCurrency: input.baseCurrency,
    amount: input.amount,
    minimumAmount: input.minimumAmount,
    rates: toRates(input.currencies),
  });

  return { balanceErrorType, isBalanceLimitBroken };
};
